import {
  AccountInfo,
  InteractionStatus,
  RedirectRequest,
  SilentRequest
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { ErrorPage, LoadingPage } from "@digital-services/shell";

import { UnAuthorizedPage } from "@account/pages/UnAuthorizedPage";

import useIdToken from "@hooks/useIdToken";
import {
  addUserRequestHeadersInterceptor,
  ejectRequestInterceptor
} from "@utils/axiosInstance";

import { loginRequest, tokenRequest } from "../../authConfig";
import { User } from "./models/User";
import { getIdTokenHint, getUserRoleName } from "./services/userService";
import { UserContext } from "./UserContext";

interface Props {
  children: ReactNode;
}

const defaultLanguage = "en";

const UserContextProvider: FunctionComponent<Props> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const account = instance.getActiveAccount();

  const { userId, userName, language, organizationId, userEmail } =
    useIdToken();

  const [user, setUser] = useState<User>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [unAuthorized, setUnAuthorized] = useState<boolean>(false);

  const { t, i18n } = useTranslation(undefined, { keyPrefix: "account" });

  const getAccessToken = useCallback(
    async (forceRefresh?: boolean): Promise<string> => {
      const account = instance.getActiveAccount();
      if (!account) {
        throw new Error("No active account");
      }

      const request: SilentRequest = {
        ...tokenRequest,
        forceRefresh: forceRefresh ?? false,
        account: account
      };

      let accessToken = "";
      try {
        const result = await instance.acquireTokenSilent(request);
        accessToken = result.accessToken;
      } catch (error) {
        // eslint-disable-next-line
        console.error(error);
      }

      if (!accessToken || accessToken === "") await instance.logoutRedirect();

      return accessToken;
    },
    [instance]
  );

  const switchOrganization = useCallback(
    async (organizationId: string): Promise<void> => {
      const accessToken = await getAccessToken();

      const idTokenHint = await getIdTokenHint(accessToken, organizationId);
      if (!idTokenHint) {
        throw new Error("IdTokenHint is NULL");
      }

      const account = instance.getActiveAccount();

      const request: RedirectRequest = {
        ...loginRequest,
        account: account!,
        extraQueryParameters: {
          id_token_hint: idTokenHint
        }
      };
      await instance.acquireTokenRedirect(request);
    },
    [getAccessToken, instance]
  );

  useEffect(() => {
    let id: number;

    const updateId = () =>
      addUserRequestHeadersInterceptor(language ?? defaultLanguage, () =>
        getAccessToken()
      );

    const initialize = async (account: AccountInfo | null) => {
      if (account === null || !userId) {
        setUser(undefined);
        return;
      }

      const accessToken = await getAccessToken(true);

      getUserRoleName(accessToken, language ?? defaultLanguage).then(
        (roleName: string | null) => {
          if (roleName === null) {
            setUnAuthorized(true);
          }
          setUser(
            new User(
              userId,
              userName ?? "",
              roleName ?? "",
              language ?? defaultLanguage,
              userEmail ?? ""
            )
          );
          id = updateId();
        }
      );
    };
    if (inProgress !== InteractionStatus.None) {
      // eslint-disable-next-line
      console.log("Initialize userContext: Busy");
      return;
    }

    setIsLoading(true);
    initialize(account)
      .catch(error => {
        // eslint-disable-next-line
        console.log(error);
        setError(t("signin.user.load.error.message"));
      })
      .finally(() => setIsLoading(false));

    return () => {
      ejectRequestInterceptor(id);
    };

    // eslint-disable-next-line
  }, [userId, organizationId, inProgress]);

  useEffect(() => {
    const initialize = async (user: User | undefined) => {
      const userLanguage = user?.language ?? defaultLanguage;

      await i18n.changeLanguage(userLanguage);
    };

    // eslint-disable-next-line
    initialize(user).catch(reason => console.error(reason));
  }, [user, i18n]);

  const contextValue = useMemo(
    () => ({
      user: user!,
      getAccessToken,
      switchOrganization
    }),
    [user, getAccessToken, switchOrganization]
  );

  const memoizedChildren = useMemo(
    () => <>{children}</>,
    // eslint-disable-next-line
    []
  );

  if (isLoading) return <LoadingPage message="" />;

  if (unAuthorized) return <UnAuthorizedPage />;
  if (error) return <ErrorPage title="" message={error} />;

  if (!user) return <LoadingPage message="" />;

  return (
    <UserContext.Provider value={contextValue}>
      {memoizedChildren}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
